import { Children, type FC, isValidElement, type ReactNode } from 'react';
import { cx } from '@linaria/atomic';
import { Icon, type IconName } from '@frontend/icons';
import { Text } from '@frontend/design-system';
import { styles } from '../feature-promotion-banner.styles';
import type { FeaturePromotionBannerSeparator } from '../feature-promotion-banner.types';

export interface FeaturePromotionBannerBulletProps {
  className?: string;
}

export const FeaturePromotionBannerBullet: FC<FeaturePromotionBannerBulletProps> = ({ className }) => {
  return (
    <span className={cx(styles.bullet, className)} aria-hidden='true'>
      •
    </span>
  );
};

export interface FeaturePromotionBannerHighlightProps {
  children: ReactNode;
  icon?: IconName | ReactNode;
  iconColor?: string;
  showIconBackground?: boolean;
  className?: string;
  testId?: string;
}

export const FeaturePromotionBannerHighlight: FC<FeaturePromotionBannerHighlightProps> = ({
  children,
  icon,
  iconColor,
  showIconBackground = false,
  className,
  testId,
}) => {
  const iconClassName = cx(styles.highlightIcon, showIconBackground && styles.highlightIconBackground);

  const iconContent = typeof icon === 'string' ? <Icon name={icon as IconName} /> : isValidElement(icon) ? icon : null;

  const iconElement = iconContent ? (
    <span className={iconClassName} style={iconColor ? { color: iconColor } : undefined}>
      {iconContent}
    </span>
  ) : null;

  return (
    <span className={cx(styles.highlightItem, className)} data-testid={testId}>
      {iconElement}
      <Text as='span' size='small' color='light'>
        {children}
      </Text>
    </span>
  );
};

export interface FeaturePromotionBannerHighlightsProps {
  children: ReactNode;
  separator?: FeaturePromotionBannerSeparator;
  className?: string;
  testId?: string;
}

export const FeaturePromotionBannerHighlights: FC<FeaturePromotionBannerHighlightsProps> = ({
  children,
  separator = 'none',
  className,
  testId,
}) => {
  const childArray = Children.toArray(children).filter(Boolean);

  const renderedContent =
    separator === 'bullet'
      ? childArray.reduce<ReactNode[]>((acc, child, index) => {
          if (index > 0) {
            acc.push(<FeaturePromotionBannerBullet key={`bullet-${index}`} />);
          }
          acc.push(child);
          return acc;
        }, [])
      : childArray;

  return (
    <div
      className={cx(
        styles.highlights,
        separator === 'bullet' ? styles.highlightsBullets : styles.highlightsNoBullets,
        className
      )}
      data-testid={testId}
    >
      {renderedContent}
    </div>
  );
};
